import {
  Alert,
  Button,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Space,
  Tag,
  Typography,
} from "antd";
import Icon from "@ant-design/icons";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Icons } from "../assets/icons";
import { FormMode } from "./InModal.default";

// A threshold this large means "and above" (the top/worst band) — rendered as ∞.
const INFINITY_SENTINEL = 100000;

// editingKey sentinel for "adding a brand-new band" (a blank inline editor, not yet in the buffer).
const NEW_KEY = "__new_band__";

export type BandFieldKey =
  | "label"
  | "threshold"
  | "color"
  | "readinessMultiplier"
  | "interpretation"
  | "recommendedAction";

export interface BandColumn {
  readonly key: BandFieldKey;
  readonly label: string;
  readonly type: "text" | "number" | "color" | "textarea";
  // Optional fixed help shown under a textarea field (e.g. "what to do when a subject lands here").
  readonly placeholder?: string;
  // Optional hover help shown as a "?" icon next to the field label.
  readonly tooltip?: string;
}

// One band in the editor's buffer. `__type` tracks the pending persistence action (the proven junction idiom).
export interface BandRow {
  readonly key: string;
  __type: "add" | "existing" | "update" | "delete";
  uid?: string;
  rowVersion?: string;
  label?: string;
  threshold?: number;
  color?: string;
  readinessMultiplier?: number;
  interpretation?: string;
  recommendedAction?: string;
}

export interface BandListEditorHandle {
  // Flushes buffered add/update/delete against the given owner uid. Host calls this on save.
  persist(ownerUid: string): Promise<unknown>;
}

// Maps loaded band entities (KPI ranges / metric bands / report bands) into the editor's BandRow shape. Each owner
// supplies how to read its threshold (Range.threshold vs Band.upperThreshold); the rest of the fields are shared.
export function toBandRows<
  T extends {
    uid: string;
    rowVersion?: string;
    label?: string;
    color?: string;
    interpretation?: string;
    recommendedAction?: string;
    readinessMultiplier?: number;
  },
>(items: readonly T[], thresholdOf: (item: T) => number | undefined): BandRow[] {
  return items.map((i) => ({
    key: i.uid,
    __type: "existing",
    uid: i.uid,
    rowVersion: i.rowVersion,
    label: i.label,
    threshold: thresholdOf(i),
    color: i.color,
    interpretation: i.interpretation,
    recommendedAction: i.recommendedAction,
    readinessMultiplier: i.readinessMultiplier,
  }));
}

export interface BandListEditorProps {
  readonly mode: FormMode;
  // The input field name the owner uid is sent under: "matrixUid" | "metricDefinitionUid" | "reportUid".
  readonly ownerKeyName: string;
  // The input field name the numeric threshold is sent under: "threshold" | "upperThreshold".
  readonly thresholdKeyName: "threshold" | "upperThreshold";
  readonly columns: readonly BandColumn[];
  readonly initial?: readonly BandRow[];
  readonly onAdd: (input: any) => Promise<unknown>;
  readonly onUpdate: (input: any) => Promise<unknown>;
  readonly onDelete: (uid: string) => Promise<unknown>;
  readonly height?: number;
}

interface BandFormValues {
  label?: string;
  threshold?: number;
  color?: string;
  readinessMultiplier?: number;
  interpretation?: string;
  recommendedAction?: string;
}

function thresholdText(v?: number): string {
  if (v == null) return "—";
  return v >= INFINITY_SENTINEL ? "∞" : `${v}`;
}

// Folds an un-committed inline-form entry into the row buffer so a band typed but not yet "Add"ed/"Save"d isn't lost
// when the host saves. Only commits when a label was entered (avoids persisting an empty row).
function withPendingRow(
  rows: BandRow[],
  v: BandFormValues,
  editingKey: string | null,
): BandRow[] {
  if (!v || v.label == null || v.label === "") return rows;
  if (editingKey && editingKey !== NEW_KEY)
    return rows.map((r) =>
      r.key === editingKey
        ? { ...r, ...v, __type: r.__type === "add" ? "add" : "update" }
        : r,
    );
  return [...rows, { key: `pending-${rows.length}`, __type: "add", ...v }];
}

// A config-driven, buffered band editor reused for KPI matrix ranges, metric bands and report bands. It manages the
// add/edit/delete buffer in memory and flushes it via `persist(ownerUid)` so the host controls WHEN bands are saved
// (KPI/report flush as part of the parent dialog's save; metrics flush from a dedicated band dialog).
const BandListEditor = forwardRef<BandListEditorHandle, BandListEditorProps>(
  function BandListEditor(props, ref) {
    const [form] = Form.useForm<BandFormValues>();
    const [rows, setRows] = useState<BandRow[]>(() =>
      (props.initial ?? []).map((r) => ({ ...r })),
    );
    const [editingKey, setEditingKey] = useState<string | null>(null);

    const has = useMemo(
      () => (key: BandFieldKey) => props.columns.some((c) => c.key === key),
      [props.columns],
    );
    const tip = useMemo(
      () => (key: BandFieldKey) =>
        props.columns.find((c) => c.key === key)?.tooltip,
      [props.columns],
    );

    const readOnly = props.mode === "view" || props.mode === "delete";
    const visible = rows.filter((r) => r.__type !== "delete");

    // Non-blocking warning: thresholds should be present and non-duplicated for predictable resolution.
    const thresholdWarning = useMemo(() => {
      if (visible.some((r) => r.threshold == null))
        return "A band has no threshold — it can't be resolved. Give every band a numeric threshold.";
      const ts = visible
        .map((r) => r.threshold)
        .filter((t): t is number => t != null)
        .sort((a, b) => a - b);
      const dup = ts.some((t, i) => i > 0 && t === ts[i - 1]);
      return dup
        ? "Two bands share the same threshold — resolution picks the first match; make thresholds distinct."
        : null;
    }, [visible]);

    function buildInput(r: BandRow, ownerUid: string, withUid: boolean) {
      const input: any = {
        [props.ownerKeyName]: ownerUid,
        label: r.label,
        color: r.color,
        [props.thresholdKeyName]: r.threshold,
      };
      if (has("readinessMultiplier"))
        input.readinessMultiplier = r.readinessMultiplier;
      if (has("interpretation")) input.interpretation = r.interpretation;
      if (has("recommendedAction"))
        input.recommendedAction = r.recommendedAction;
      if (withUid) {
        input.uid = r.uid;
        input.rowVersion = r.rowVersion;
        // An update never moves a band to another owner, and no band update request accepts the owner key:
        // sending it made the server refuse the whole patch (400), so an existing band could not be edited.
        delete input[props.ownerKeyName];
      }
      return input;
    }

    useImperativeHandle(
      ref,
      () => ({
        persist(ownerUid: string) {
          // Capture any band the user typed but didn't explicitly add/save before hitting the host's Save.
          const effective = withPendingRow(
            rows,
            form.getFieldsValue(),
            editingKey,
          );
          const promises: Promise<unknown>[] = [];
          effective.forEach((r) => {
            if (r.__type === "delete" && r.uid)
              promises.push(props.onDelete(r.uid));
            else if (r.__type === "add")
              promises.push(props.onAdd(buildInput(r, ownerUid, false)));
            else if (r.__type === "update" && r.uid)
              promises.push(props.onUpdate(buildInput(r, ownerUid, true)));
          });
          return Promise.all(promises);
        },
      }),
      [rows, editingKey, props],
    );

    function onAddClick(): void {
      form.resetFields();
      setEditingKey(NEW_KEY);
    }

    function onSubmitRow(): void {
      const v = form.getFieldsValue();
      if (editingKey && editingKey !== NEW_KEY) {
        setRows((prev) =>
          prev.map((r) =>
            r.key === editingKey
              ? {
                  ...r,
                  ...v,
                  __type: r.__type === "add" ? "add" : "update",
                }
              : r,
          ),
        );
      } else {
        setRows((prev) => [
          ...prev,
          { key: `new-${prev.length}-${Date.now()}`, __type: "add", ...v },
        ]);
      }
      setEditingKey(null);
      form.resetFields();
    }

    function onEditRow(row: BandRow): void {
      setEditingKey(row.key);
      form.setFieldsValue({
        label: row.label,
        threshold: row.threshold,
        color: row.color,
        readinessMultiplier: row.readinessMultiplier,
        interpretation: row.interpretation,
        recommendedAction: row.recommendedAction,
      });
    }

    function onCancelEdit(): void {
      setEditingKey(null);
      form.resetFields();
    }

    function onRemoveRow(row: BandRow): void {
      setRows((prev) =>
        prev
          .map((r) =>
            r.key === row.key
              ? r.__type === "add"
                ? null
                : { ...r, __type: "delete" as const }
              : r,
          )
          .filter((r): r is BandRow => r != null),
      );
      if (editingKey === row.key) onCancelEdit();
    }

    // The field block renders inline — either as a blank "new band" row (added via the button) or in place of the
    // row being edited — so the editing controls always sit where the user is working, never detached at the top.
    const fieldsBlock = () => (
      <div className="flex w-full flex-wrap items-end gap-2">
        {has("label") && (
          <Form.Item
            className="m-0 min-w-[7rem] flex-1"
            label="Label"
            name="label"
            tooltip={tip("label")}
          >
            <Input />
          </Form.Item>
        )}
        {has("threshold") && (
          <Form.Item
            className="m-0 w-28"
            label="Threshold"
            name="threshold"
            tooltip={
              tip("threshold") ??
              "Inclusive upper bound — a value falls in the first band it does not exceed. Use a very large number (∞) for the top/last band."
            }
          >
            <InputNumber className="w-full" />
          </Form.Item>
        )}
        {has("readinessMultiplier") && (
          <Form.Item
            className="m-0 w-32"
            label="Readiness ×"
            name="readinessMultiplier"
            tooltip={
              tip("readinessMultiplier") ??
              "Multiplier (0–1) this band applies to the metric's weight in the readiness index (e.g. 🟢1.0 / 🟠0.75 / 🔴0.6)."
            }
          >
            <InputNumber className="w-full" step={0.05} min={0} max={1} />
          </Form.Item>
        )}
        {has("color") && (
          <Form.Item
            className="m-0"
            label="Color"
            name="color"
            tooltip={tip("color")}
            getValueFromEvent={(c) =>
              typeof c === "string" ? c : c?.toHexString?.()
            }
          >
            <ColorPicker format="hex" />
          </Form.Item>
        )}
        {has("interpretation") && (
          <Form.Item
            className="m-0 w-full"
            label="Interpretation (what this band means)"
            name="interpretation"
            tooltip={
              tip("interpretation") ??
              "Plain-language meaning of landing in this band — shown in the exported report PDF."
            }
          >
            <Input.TextArea rows={3} style={{ resize: "vertical" }} />
          </Form.Item>
        )}
        {has("recommendedAction") && (
          <Form.Item
            className="m-0 w-full"
            label="Recommended action (what to do)"
            name="recommendedAction"
            tooltip={
              tip("recommendedAction") ??
              "Suggested next step for a subject in this band — shown in the exported report PDF."
            }
          >
            <Input.TextArea rows={3} style={{ resize: "vertical" }} />
          </Form.Item>
        )}
        <Form.Item className="m-0">
          <Space>
            <Button type="primary" onClick={onSubmitRow}>
              Save band
            </Button>
            <Button onClick={onCancelEdit}>Cancel</Button>
          </Space>
        </Form.Item>
      </div>
    );

    const ordered = [...visible].sort(
      (a, b) => (a.threshold ?? 0) - (b.threshold ?? 0),
    );

    const adding = editingKey === NEW_KEY;
    const isEmpty = ordered.length === 0 && !adding;

    return (
      <Form form={form} autoComplete="off" layout="vertical" component={false}>
        <div className="flex flex-col gap-2">
          {thresholdWarning && (
            <Alert type="warning" showIcon message={thresholdWarning} />
          )}

          <div
            className="overflow-y-auto rounded-md border-2 border-solid border-gray-300"
            style={{ maxHeight: props.height ?? 320 }}
          >
            {ordered.map((item) =>
              editingKey === item.key ? (
                // Inline editor — sits exactly where the row is.
                <div
                  key={item.key}
                  className="border-b border-gray-100 bg-amber-50 p-2 last:border-b-0"
                >
                  {fieldsBlock()}
                </div>
              ) : (
                <div
                  key={item.key}
                  className="flex items-center gap-2 border-b border-gray-100 px-2 py-1.5 last:border-b-0"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Tag color={item.color}>{item.label || "—"}</Tag>
                    <Typography.Text type="secondary">
                      ≤ {thresholdText(item.threshold)}
                    </Typography.Text>
                    {has("readinessMultiplier") &&
                      item.readinessMultiplier != null && (
                        <Typography.Text type="secondary">
                          · ×{item.readinessMultiplier}
                        </Typography.Text>
                      )}
                    {item.interpretation && (
                      <Typography.Text
                        ellipsis
                        className="min-w-0 flex-1 text-gray-500"
                      >
                        {item.interpretation}
                      </Typography.Text>
                    )}
                  </div>
                  {!readOnly && (
                    <Space>
                      <Button
                        type="text"
                        onClick={() => onEditRow(item)}
                        icon={
                          <Icon className="text-lg" component={Icons.EditIcon} />
                        }
                      />
                      <Button
                        type="text"
                        danger
                        onClick={() => onRemoveRow(item)}
                        icon={
                          <Icon
                            className="-ml-0.5 text-xl"
                            component={Icons.CloseIcon}
                          />
                        }
                      />
                    </Space>
                  )}
                </div>
              ),
            )}

            {/* Blank inline editor for a brand-new band (opened by the Add button). */}
            {adding && (
              <div className="border-b border-gray-100 bg-amber-50 p-2 last:border-b-0">
                {fieldsBlock()}
              </div>
            )}

            {isEmpty && (
              <div className="flex items-center justify-center p-6">
                <Typography.Text type="secondary">
                  No bands defined.
                </Typography.Text>
              </div>
            )}
          </div>

          {!readOnly && !editingKey && (
            <Button
              type="dashed"
              block
              onClick={onAddClick}
              icon={<Icon component={Icons.AddIcon} />}
            >
              Add band
            </Button>
          )}
        </div>
      </Form>
    );
  },
);

export default BandListEditor;
